import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, createStyles, Group, Text, Tooltip} from '@mantine/core';
import React from 'react';

import {QueryingChartContent} from 'charts/chart-content/ChartContent';
import {ArtifactHeaderSlots, ArtifactType, getArtifactTypeLabel} from 'llm/chat-library/ArtifactTypes';
import type {CanvasArtifact} from 'llm/chat-library/useCanvasArtifact';
import type {DocumentArtifact} from 'llm/chat-library/useDocumentArtifact';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {LocalTime} from 'ui-library/Times';

import {FileViewerContent} from '../analysis-workspace/FileViewerContent';
import type {PreviewableSidebarItem} from './useSidebarItems';

/**
 * The uppercase eyebrow label shown above the detail title, naming what kind of thing is open (e.g.
 * "DRAFT DOCUMENT", "CANVAS", "FILE"). Derived from the item's artifact type, since the backend does
 * not carry a separate status field for these rows.
 */
function eyebrowLabel(item: PreviewableSidebarItem): string {
  if (item.kind === 'artifact') {
    return getArtifactTypeLabel(item.artifactType).toUpperCase();
  }
  if (item.kind === 'chart') {
    return 'CHART';
  }
  return 'FILE';
}

/** The detail view for a single artifact/file, with a back button to the list. */
export function RightSidebarItemDetail({
  item,
  primaryArtifact,
  canvasArtifact,
  isEditable,
  onBack,
  onClose,
  canExpand,
  isExpanded,
  onToggleExpand,
}: {
  item: PreviewableSidebarItem;
  primaryArtifact: DocumentArtifact | null;
  canvasArtifact: CanvasArtifact | null;
  isEditable: boolean;
  onBack: () => void;
  onClose: () => void;
  /** Whether to offer the expand control (false in the narrow view, which is always full-panel). */
  canExpand: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
}): JSX.Element {
  const {classes} = useStyles();
  // Artifacts render this title row in place of their own header, contributing their badges and
  // actions, so the detail view has a single header. Files and loading artifacts have none.
  const renderTitleRow = ({badges, actions}: ArtifactHeaderSlots): JSX.Element => (
    <div className={classes.titleRow}>
      <Tooltip label="Back to sidebar" withArrow={true}>
        <ActionIcon
          variant="subtle"
          size="md"
          onClick={onBack}
          aria-label="Back to sidebar"
          {...getTestProps({component: 'RightSidebarLayout', element: 'back-button'})}
        >
          <BenchlingIcon icon="arrow-left" />
        </ActionIcon>
      </Tooltip>
      <div className={classes.titleText}>
        <Group spacing="xs" noWrap={true}>
          <Text fw={600} size="sm" className={classes.detailTitle}>
            {item.label}
          </Text>
          {badges}
        </Group>
        {(item.action != null || item.lastTouchedAt != null) && (
          <Text size="xs" color="dimmed" className={classes.detailSubtitle}>
            {item.action}
            {item.action != null && item.lastTouchedAt != null && ' · '}
            {item.lastTouchedAt != null && <LocalTime datetime={item.lastTouchedAt} />}
          </Text>
        )}
      </div>
      {actions != null && (
        <Group spacing="xs" noWrap={true} className={classes.titleActions}>
          {actions}
        </Group>
      )}
    </div>
  );
  return (
    <>
      <div className={classes.eyebrowBar}>
        <Text className={classes.eyebrowLabel} size="xs" fw={600}>
          {eyebrowLabel(item)}
        </Text>
        <div className={classes.eyebrowActions}>
          {canExpand && (
            <Tooltip label={isExpanded ? 'Restore sidebar' : 'Expand sidebar'} withArrow={true}>
              <ActionIcon
                variant="subtle"
                size="md"
                onClick={onToggleExpand}
                aria-label={isExpanded ? 'Restore sidebar' : 'Expand sidebar'}
                aria-pressed={isExpanded}
                {...getTestProps({component: 'RightSidebarLayout', element: 'expand-button'})}
              >
                <BenchlingIcon icon={isExpanded ? 'collapse-right-full' : 'expand-right-full'} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip label="Close sidebar" withArrow={true}>
            <ActionIcon
              variant="subtle"
              size="md"
              onClick={onClose}
              aria-label="Close sidebar"
              {...getTestProps({component: 'RightSidebarLayout', element: 'close-button'})}
            >
              <BenchlingIcon icon="close" />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>
      <div
        className={classes.detailBody}
        {...getTestProps({component: 'RightSidebarLayout', element: 'detail'})}
      >
        {item.kind === 'artifact' ? (
          <ArtifactDetail
            item={item}
            primaryArtifact={primaryArtifact}
            canvasArtifact={canvasArtifact}
            isEditable={isEditable}
            renderHeader={renderTitleRow}
          />
        ) : item.kind === 'chart' ? (
          <>
            {renderTitleRow({})}
            <div
              className={classes.chartPreview}
              {...getTestProps({
                component: 'RightSidebarLayout',
                element: 'chart-preview',
                key: item.chartId,
              })}
              data-amp-safe={false}
            >
              <QueryingChartContent chartId={item.chartId} />
            </div>
          </>
        ) : (
          <>
            {renderTitleRow({})}
            <FileViewerContent
              content={{
                chatSessionId: item.chatSessionId,
                externalFileId: item.externalFileId,
                filename: item.filename,
                mimeType: item.mimeType,
                label: item.label,
              }}
            />
          </>
        )}
      </div>
    </>
  );
}

/**
 * Renders the live editor for an artifact row. The backend lists the artifact by its externalId; the
 * matching live view is resolved here from the streamed artifact state (the same instance the chat
 * uses), keyed by that id. Until it has streamed in, the row exists but has no live view yet, so we
 * show a loading state rather than a blank pane.
 */
function ArtifactDetail({
  item,
  primaryArtifact,
  canvasArtifact,
  isEditable,
  renderHeader,
}: {
  item: Extract<PreviewableSidebarItem, {kind: 'artifact'}>;
  primaryArtifact: DocumentArtifact | null;
  canvasArtifact: CanvasArtifact | null;
  isEditable: boolean;
  renderHeader: (slots: ArtifactHeaderSlots) => JSX.Element;
}): JSX.Element {
  if (
    item.artifactType === ArtifactType.DOCUMENT &&
    primaryArtifact != null &&
    primaryArtifact.data.document_api_identifier === item.id
  ) {
    const DocumentComponent = primaryArtifact.Component;
    return (
      <DocumentComponent data={primaryArtifact.data} editable={isEditable} renderHeader={renderHeader} />
    );
  }
  if (
    item.artifactType === ArtifactType.CANVAS &&
    canvasArtifact != null &&
    canvasArtifact.data.canvasDefinitionId === item.id
  ) {
    const CanvasComponent = canvasArtifact.Component;
    return <CanvasComponent data={canvasArtifact.data} editable={isEditable} renderHeader={renderHeader} />;
  }
  return (
    <>
      {renderHeader({})}
      <CenteredHorizontalThrobber />
    </>
  );
}

const useStyles = createStyles((theme) => ({
  eyebrowBar: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
  },
  eyebrowLabel: {
    color: theme.colors.gray[6],
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  eyebrowActions: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  titleRow: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
  },
  titleText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xxs,
  },
  titleActions: {
    flexShrink: 0,
  },
  detailTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  detailSubtitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  detailBody: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  chartPreview: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
}));
